import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { listAssistantExchanges, type AssistantExchangeOutcome, type AssistantExchangeRow } from "../api/rest";
import { useSiteStore } from "../stores/siteStore";
import css from "./WarehouseAdminPage.module.css";

/**
 * The assistant's unanswered questions (ADR 412).
 *
 * When no engine answers, the model calls `report_gap` with a topic and what would
 * be needed; the loop stores the exchange as `no_engine`. This page is that list —
 * the backlog for the next engines — with the other outcomes one filter away,
 * because a badly answered question is a gap too.
 *
 * Same shape and gate as the audit trail: a flat table, global admin only, and a
 * refusal that never looks like an empty list.
 */
export function AssistantGapsPage() {
  const { t } = useTranslation();
  const sites = useSiteStore((s) => s.availableSites);
  const [outcome, setOutcome] = useState<AssistantExchangeOutcome | "">("no_engine");

  const query = useQuery({
    queryKey: ["assistant-exchanges", outcome],
    queryFn: () => listAssistantExchanges({ outcome: outcome || undefined }),
    retry: false,
  });

  const siteName = (id: string) => sites.find((s) => s.id === id)?.name ?? id;

  return (
    <div className={css.root}>
      <div className={css.header}>
        <h1 className={css.title}>{t("assistantGaps.title")}</h1>
      </div>
      <p className={css.notice}>{t("assistantGaps.intro")}</p>

      <div className={css.form}>
        <div className={css.field}>
          <label className={css.label} htmlFor="assistant-gaps-outcome">{t("assistantGaps.outcomeFilter")}</label>
          <select id="assistant-gaps-outcome" className={css.input} value={outcome}
                  onChange={(e) => setOutcome(e.target.value as AssistantExchangeOutcome | "")}>
            <option value="no_engine">{t("assistantGaps.outcome.no_engine")}</option>
            <option value="answered">{t("assistantGaps.outcome.answered")}</option>
            <option value="refusal">{t("assistantGaps.outcome.refusal")}</option>
            <option value="error">{t("assistantGaps.outcome.error")}</option>
            <option value="">{t("assistantGaps.outcome.all")}</option>
          </select>
        </div>
      </div>

      {query.isLoading && <p className={css.notice}>{t("common.loading")}</p>}

      {query.isError && (
        <p className={css.notice} data-testid="assistant-gaps-error">{t("assistantGaps.denied")}</p>
      )}

      {query.data?.length === 0 && (
        <p className={css.notice} data-testid="assistant-gaps-empty">{t("assistantGaps.empty")}</p>
      )}

      {!!query.data?.length && (
        <table className={css.table} data-testid="assistant-gaps-table">
          <thead>
            <tr>
              <th>{t("assistantGaps.col.date")}</th>
              <th>{t("assistantGaps.col.site")}</th>
              <th>{t("assistantGaps.col.page")}</th>
              <th>{t("assistantGaps.col.question")}</th>
              <th>{t("assistantGaps.col.need")}</th>
              <th>{t("assistantGaps.col.outcome")}</th>
            </tr>
          </thead>
          <tbody>
            {query.data.map((e) => <Row key={e.id} e={e} siteName={siteName} />)}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Row({ e, siteName }: { e: AssistantExchangeRow; siteName: (id: string) => string }) {
  const { t } = useTranslation();
  return (
    <tr>
      <td>{formatStamp(e.askedAt)}</td>
      <td>{siteName(e.siteId)}</td>
      <td>{e.page ? <code>{e.page}</code> : "—"}</td>
      <td>{e.question}</td>
      <td>
        {e.gapTopic && <strong>{e.gapTopic}</strong>}
        {e.gapTopic && e.gapNeed && <br />}
        {e.gapNeed ?? (e.gapTopic ? null : "—")}
      </td>
      <td>
        {/* A word, never only a colour (design system). */}
        <span className={css.badge}>{t(`assistantGaps.outcome.${e.outcome}`)}</span>
      </td>
    </tr>
  );
}

/** dd/MM/yyyy HH:mm in the reader's zone: the stamp is a server instant describing
 *  when a person asked, not a fact about a warehouse floor (ADR 265/266). */
export function formatStamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}
